import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import './UserGroups.css';
import { listGroups as v2ListGroups, joinGroup as v2JoinGroup, leaveGroup as v2LeaveGroup, STORAGE_KEYS } from './v2Api';

const UserGroups = ({ user }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [groupToJoin, setGroupToJoin] = useState(null);
  const navigate = useNavigate();
  const { link_key } = useParams();

  useEffect(() => {
    const fetchUserGroups = async () => {
      if (!user) {
        return;
      }
      setLoading(true);

      try {
        // get user's groups from v2
        const { groups: myGroups } = await v2ListGroups(user);
        let groupsData = myGroups || [];

        let newGroupToJoin = null;
        if (link_key) {
          // check if already a member
          const existing = groupsData.find(g => g.link_key === link_key);
          if (existing) {
            // already in group — bubble it to top
            groupsData = [existing, ...groupsData.filter(g => g.link_key !== link_key)];
          } else {
            // not a member — set groupToJoin
            newGroupToJoin = { link_key };  // we'll resolve name on join
            setGroupToJoin(newGroupToJoin);
          }
        }

        setGroups(groupsData);
      } catch (error) {
        console.error('Error fetching groups (v2):', error);
        setMessage('Error fetching groups.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserGroups();
  }, [user, link_key]);

  const handleReviewClick = (groupId) => {
    localStorage.setItem(STORAGE_KEYS.selectedGroupId, groupId);
    navigate('/options');
  };

  const handleNewGroupClick = () => {
    navigate('/new_group');
  };

  const handleJoinClick = async () => {
    if (!groupToJoin) return;

    try {
      const result = await v2JoinGroup(user, { linkKey: groupToJoin.link_key });
      localStorage.setItem(STORAGE_KEYS.selectedGroupId, result.group_id);
      navigate('/options');
    } catch (error) {
      console.error('Error joining group (v2):', error);
      setMessage(error.message || 'Error joining group.');
    }
  };

  const handleLeaveClick = async (groupId) => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;

    try {
      await v2LeaveGroup(user, { groupId });
      setGroups(groups.filter(group => group.id !== groupId));
    } catch (error) {
      console.error('Error leaving group (v2):', error);
      setMessage('Error leaving group.');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="user-groups">
      {groupToJoin && (
        <div className="join-group">
          <button className="active-button" onClick={handleJoinClick}>Join {groupToJoin.name}</button>
          {message && <p className="error-message">{message}</p>}
        </div>
      )}
      {groups.length > 0 ? (
        <div>
          <p>Your groups: {groups.map((group, index) => (
            <span key={group.id}>
              {group.name}{' '}
              <span data-testid={`group-member-count-${group.id}`} style={{ color: '#888', fontSize: '0.85em' }}>
                ({group.member_count ?? '?'} member{group.member_count !== 1 ? 's' : ''})
              </span>{' '}
              <a href="#" onClick={() => handleLeaveClick(group.id)}>(leave)</a>
              {index < groups.length - 1 ? ', ' : ''}
            </span>
          ))}</p>
          {groups.length === 1 ? (
            <button className="active-button" onClick={() => handleReviewClick(groups[0].id)}>Explore for {groups[0].name}</button>
          ) : (
            <button className="active-button" onClick={() => handleReviewClick()}>Explore for {groups.length} groups</button>
          )}
        </div>
      ) : null}
      {groups.length === 0 && !groupToJoin && (
        <button
          className="active-button"
          data-testid="start-exploring-btn"
          onClick={() => navigate('/options')}
        >
          Start exploring
        </button>
      )}
      {!loading && (
        <button
          className="secondary-button"
          data-testid="create-group-btn"
          onClick={handleNewGroupClick}
        >
          {groups.length === 0 && !groupToJoin
            ? '…or create a group to combine picks with friends'
            : 'Create new group'}
        </button>
      )}
      {message && <p className="error-message">{message}</p>}
    </div>
  );
};

export default UserGroups;
